import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@/lib/server-fn-compat";
import { uploadMedia } from "@/lib/uploads";
import { MEDIA_TARGETS } from "@/lib/media-buckets";
import {
  addAttachment,
  addComment,
  deleteIssue,
  ISSUE_PRIORITIES,
  ISSUE_SEVERITIES,
  ISSUE_STATUSES,
  ISSUE_TYPES,
  removeAttachment,
  updateIssue,
} from "@/lib/issues.functions";
import { issueQueryOptions, labelOf, PRIORITY_COLORS, SEVERITY_COLORS, STATUS_COLORS } from "@/lib/issues.queries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { ArrowLeft, Check, Trash2, Upload, Link as LinkIcon, X } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/issues/$id")({
  component: IssueDetailPage,
});

function IssueDetailPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const nav = useNavigate();
  const { data: issue, isLoading } = useQuery(issueQueryOptions(id));
  const update = useServerFn(updateIssue);
  const del = useServerFn(deleteIssue);
  const comment = useServerFn(addComment);
  const addAtt = useServerFn(addAttachment);
  const delAtt = useServerFn(removeAttachment);

  const [commentBody, setCommentBody] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkLabel, setLinkLabel] = useState("");
  const [linkKind, setLinkKind] = useState<"video_link" | "external_link">("external_link");
  const fileInput = useRef<HTMLInputElement>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["issues", id] });

  const patchMut = useMutation({
    mutationFn: async (patch: any) => update({ data: { id, patch } }),
    onSuccess: () => { invalidate(); qc.invalidateQueries({ queryKey: ["issues"] }); toast.success("Saved"); },
    onError: (e: any) => toast.error(e.message),
  });

  const commentMut = useMutation({
    mutationFn: async () => comment({ data: { issue_id: id, body: commentBody } }),
    onSuccess: () => { setCommentBody(""); invalidate(); toast.success("Comment added"); },
    onError: (e: any) => toast.error(e.message),
  });

  const linkMut = useMutation({
    mutationFn: async () =>
      addAtt({ data: { issue_id: id, kind: linkKind, url: linkUrl, label: linkLabel || null } }),
    onSuccess: () => { setLinkUrl(""); setLinkLabel(""); invalidate(); toast.success("Link added"); },
    onError: (e: any) => toast.error(e.message),
  });

  const uploadMut = useMutation({
    mutationFn: async (file: File) => {
      // Private tree: any type, sanitised key under <issue id>/, never
      // registered in the media library. The stored value is the
      // bucket-relative path the issues API presigns on read.
      const result = await uploadMedia(file, { ...MEDIA_TARGETS.issueAttachment, prefix: id });
      const isImage = file.type.startsWith("image/");
      await addAtt({
        data: {
          issue_id: id,
          kind: isImage ? "image" : "file",
          url: result.path,
          label: file.name,
          mime_type: file.type,
          size_bytes: file.size,
        },
      });
    },
    onSuccess: () => { invalidate(); toast.success("Uploaded"); if (fileInput.current) fileInput.current.value = ""; },
    onError: (e: any) => toast.error(e.message),
  });

  const removeAttMut = useMutation({
    mutationFn: async (aid: string) => delAtt({ data: { id: aid } }),
    onSuccess: () => { invalidate(); toast.success("Removed"); },
    onError: (e: any) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: async () => del({ data: { id } }),
    onSuccess: () => { toast.success("Deleted"); nav({ to: "/admin/issues" }); },
    onError: (e: any) => toast.error(e.message),
  });

  if (isLoading || !issue) return <div className="p-6">Loading…</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/admin/issues"><ArrowLeft className="w-4 h-4 mr-1" />Back</Link>
          </Button>
          <span className="font-mono text-sm text-muted-foreground">{issue.ticket_number}</span>
        </div>
        <Button variant="destructive" size="sm" onClick={() => { if (confirm("Delete this issue?")) deleteMut.mutate(); }}>
          <Trash2 className="w-4 h-4 mr-1" />Delete
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader>
              <Input className="text-2xl font-bold border-0 px-0 focus-visible:ring-0" defaultValue={issue.title}
                onBlur={(e) => e.target.value !== issue.title && patchMut.mutate({ title: e.target.value })} />
            </CardHeader>
            <CardContent>
              <Label>Description</Label>
              <Textarea rows={6} defaultValue={issue.description ?? ""}
                onBlur={(e) => e.target.value !== (issue.description ?? "") && patchMut.mutate({ description: e.target.value })} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Attachments</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-3">
                {issue.attachments.length === 0 && <div className="text-sm text-muted-foreground">No attachments yet</div>}
                {issue.attachments.map((a: any) => (
                  <div key={a.id} className="border rounded p-2 w-48 relative group">
                    <Button size="icon" variant="destructive" className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100"
                      onClick={() => removeAttMut.mutate(a.id)}><X className="w-3 h-3" /></Button>
                    {a.kind === "image" && a.signed_url ? (
                      <a href={a.signed_url} target="_blank" rel="noopener noreferrer">
                        <img src={a.signed_url} alt={a.label ?? ""} className="w-full h-32 object-cover rounded" />
                      </a>
                    ) : (
                      <a href={a.signed_url ?? a.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm py-2">
                        <LinkIcon className="w-4 h-4" />
                        <span className="truncate">{a.label || a.url}</span>
                      </a>
                    )}
                    <div className="text-xs text-muted-foreground mt-1">{labelOf(a.kind)}</div>
                  </div>
                ))}
              </div>
              <Separator />
              <div className="space-y-2">
                <Label>Upload file / image</Label>
                <Input ref={fileInput} type="file" onChange={(e) => e.target.files?.[0] && uploadMut.mutate(e.target.files[0])} disabled={uploadMut.isPending} />
              </div>
              <div className="space-y-2">
                <Label>Add link (video, external)</Label>
                <div className="flex gap-2">
                  <Select value={linkKind} onValueChange={(v: any) => setLinkKind(v)}>
                    <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="external_link">External Link</SelectItem>
                      <SelectItem value="video_link">Video Link</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input placeholder="https://..." value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} />
                  <Input placeholder="Label (optional)" value={linkLabel} onChange={(e) => setLinkLabel(e.target.value)} />
                  <Button onClick={() => linkMut.mutate()} disabled={!linkUrl}><Upload className="w-4 h-4 mr-1" />Add</Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Comments</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {issue.comments.length === 0 && <div className="text-sm text-muted-foreground">No comments</div>}
              {issue.comments.map((c: any) => (
                <div key={c.id} className="border-l-2 border-primary pl-3">
                  <div className="text-xs text-muted-foreground">{c.author_email ?? "Unknown"} · {new Date(c.created_at).toLocaleString()}</div>
                  <div className="whitespace-pre-wrap text-sm">{c.body}</div>
                </div>
              ))}
              <Separator />
              <div className="space-y-2">
                <Textarea rows={3} value={commentBody} onChange={(e) => setCommentBody(e.target.value)} placeholder="Add a comment…" />
                <Button onClick={() => commentMut.mutate()} disabled={!commentBody.trim() || commentMut.isPending}>Post Comment</Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* RIGHT */}
        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle>Details</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <FieldSelect label="Status" value={issue.status} options={ISSUE_STATUSES as any} colors={STATUS_COLORS}
                onChange={(v) => patchMut.mutate({ status: v })} />
              <FieldSelect label="Type" value={issue.type} options={ISSUE_TYPES as any}
                onChange={(v) => patchMut.mutate({ type: v })} />
              <FieldSelect label="Severity" value={issue.severity} options={ISSUE_SEVERITIES as any} colors={SEVERITY_COLORS}
                onChange={(v) => patchMut.mutate({ severity: v })} />
              <FieldSelect label="Priority" value={issue.priority} options={ISSUE_PRIORITIES as any} colors={PRIORITY_COLORS}
                onChange={(v) => patchMut.mutate({ priority: v })} />
              <div>
                <Label>Section</Label>
                <Input defaultValue={issue.section ?? ""} onBlur={(e) => e.target.value !== (issue.section ?? "") && patchMut.mutate({ section: e.target.value || null })} />
              </div>
              <div>
                <Label>Assignee (user ID)</Label>
                <Input defaultValue={issue.assigned_to ?? ""} placeholder="uuid" onBlur={(e) => e.target.value !== (issue.assigned_to ?? "") && patchMut.mutate({ assigned_to: e.target.value || null })} />
                {issue.assignee_email && <div className="text-xs text-muted-foreground mt-1">{issue.assignee_email}</div>}
              </div>
              <div>
                <Label>Due Date</Label>
                <Input type="date" defaultValue={issue.due_date ? issue.due_date.split("T")[0] : ""}
                  onBlur={(e) => patchMut.mutate({ due_date: e.target.value ? new Date(e.target.value).toISOString() : null })} />
              </div>
              <div>
                <Label>Engineering Estimate (hours)</Label>
                <Input type="number" step="0.25" defaultValue={issue.engineering_estimate_hours ?? ""}
                  onBlur={(e) => patchMut.mutate({ engineering_estimate_hours: e.target.value ? Number(e.target.value) : null })} />
              </div>
              <Separator />
              <div className="space-y-1 text-xs">
                <div><span className="text-muted-foreground">Reported:</span> {new Date(issue.reported_at).toLocaleString()}</div>
                <div><span className="text-muted-foreground">Reporter:</span> {issue.reporter_email ?? "—"}</div>
                <div><span className="text-muted-foreground">Accepted:</span> {issue.accepted_at ? new Date(issue.accepted_at).toLocaleString() : "—"}</div>
                <div><span className="text-muted-foreground">Completed:</span> {issue.completed_at ? new Date(issue.completed_at).toLocaleString() : "—"}</div>
              </div>
              <div className="flex gap-2">
                {!issue.accepted_at && (
                  <Button size="sm" variant="outline" onClick={() => patchMut.mutate({ status: "accepted", accepted_at: new Date().toISOString() })}>
                    <Check className="w-4 h-4 mr-1" />Accept
                  </Button>
                )}
                {!issue.completed_at && (
                  <Button size="sm" variant="outline" onClick={() => patchMut.mutate({ status: "completed", completed_at: new Date().toISOString() })}>
                    <Check className="w-4 h-4 mr-1" />Complete
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>History</CardTitle></CardHeader>
            <CardContent>
              <ol className="space-y-3 text-xs">
                {issue.history.map((h: any) => (
                  <li key={h.id} className="border-l-2 border-muted pl-3">
                    <div className="font-medium">{labelOf(h.event_type)}{h.field_name ? ` · ${h.field_name}` : ""}</div>
                    {(h.from_value || h.to_value) && (
                      <div className="text-muted-foreground">
                        {h.from_value && <span className="line-through">{h.from_value}</span>}
                        {h.from_value && h.to_value && " → "}
                        {h.to_value && <span>{h.to_value}</span>}
                      </div>
                    )}
                    {h.notes && <div className="text-muted-foreground italic">{h.notes}</div>}
                    <div className="text-muted-foreground">{h.actor_email ?? "system"} · {new Date(h.created_at).toLocaleString()}</div>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function FieldSelect({ label, value, options, colors, onChange }: {
  label: string;
  value: string;
  options: readonly string[];
  colors?: Record<string, string>;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {options.map((o) => <SelectItem key={o} value={o}>{labelOf(o)}</SelectItem>)}
          </SelectContent>
        </Select>
        {colors && <Badge className={colors[value]}>{labelOf(value)}</Badge>}
      </div>
    </div>
  );
}
